import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { SiteContent } from './types';
import { loadSiteContent } from './query';
import { sanityClient } from './client';

const ContentContext = createContext<SiteContent | null>(null);

export function useSite(): SiteContent {
  const value = useContext(ContentContext);
  if (!value) {
    throw new Error('useSite must be used within <ContentProvider>');
  }
  return value;
}

interface ContentProviderProps {
  children: ReactNode;
}

export function ContentProvider({ children }: ContentProviderProps) {
  const [content, setContent] = useState<SiteContent | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function refresh(opts?: { fresh?: boolean }) {
      try {
        const next = await loadSiteContent(opts);
        if (!cancelled) setContent(next);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    }

    refresh();

    // Stream mutations on any document this site renders. When anything
    // changes, re-fetch the query. Sanity will drop the connection on
    // network hiccups; the subscription retries automatically.
    const liveQuery = '*[_type in ["siteSettings", "hero", "about", "gallery", "capabilities", "selectWork", "serviceSection", "contactCopy"]]';
    const sub = sanityClient
      .listen(liveQuery, {}, { includeResult: false, visibility: 'query' })
      .subscribe({
        next: () => {
          // Bypass the CDN on live updates so edits appear immediately
          // (the CDN can lag ~60s behind the live API).
          if (!cancelled) refresh({ fresh: true });
        },
        error: (err) => {
          console.warn('[content] listen() disconnected; content updates will resume on refresh.', err);
        },
      });

    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-dark text-textlight p-8">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl mb-4">Unable to load content</h1>
          <p className="font-sans text-sm text-textlight/70 mb-6">
            We couldn't reach the CMS. Please try again shortly.
          </p>
          <details className="text-left text-xs text-textlight/50 font-mono">
            <summary className="cursor-pointer">Details</summary>
            <pre className="mt-2 whitespace-pre-wrap">{error.message}</pre>
          </details>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div
        role="status"
        aria-label="Loading content"
        className="min-h-screen flex items-center justify-center bg-slate-dark"
      >
        <div className="w-8 h-8 border-2 border-textlight/20 border-t-bronze rounded-full animate-spin" />
      </div>
    );
  }

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}
